// Mock implementations of the Churn & Recovery API contract (spec §6).
// Deleted (along with USE_CHURN_MOCKS) once the Laravel endpoints ship.
import moment from "moment";
import { dormantThreshold } from "../../../pages/crm/churn-recovery/churnUtils";

const d = (daysAgo) => moment().subtract(daysAgo, "days").format("YYYY-MM-DD");
const inDays = (days) => moment().add(days, "days").format("YYYY-MM-DD");

// seed: [id, name, visits, cadence_days, days_since_last_visit, customer_batch]
const seeds = [
  [9001, "Emma Clarke", 8, 35, 20, "Gold Member"],            // active, green
  [9002, "Liam Patel", 6, 42, 50, "New User"],                // active, yellow
  [9003, "Olivia Chen", 12, 30, 75, "Member"],                // active, red
  [9004, "Noah Garcia", 4, 60, 90, "Declined Member"],        // at_risk, yellow
  [9005, "Ava Thompson", 9, 45, 130, "Expire/Cancel Member"], // at_risk, red
  [9006, "Sophia Rossi", 3, 90, 120, "Member"],               // at_risk, yellow
  [9007, "Mason Lee", 7, 40, 200, "Declined Member"],         // dormant, red
  [9008, "Isabella Wong", 5, 55, 260, "Expire/Cancel Member"],// dormant, red
  [9009, "Ethan Brown", 2, 120, 300, "New User"],             // dormant, red
  [9010, "Mia Nguyen", 1, null, 40, "New User"],              // active, no cadence
  [9011, "Lucas Silva", 1, null, 220, "Member"],              // dormant, no cadence
  [9012, "Amelia Khan", 0, null, null, "New User"],           // never visited
  [9013, "Grace Okafor", 4, 120, 200, "Member"],              // at_risk — personalized dormant at 240d
];

// At-risk starts flat at 90d; the dormant boundary personalizes to the
// client's own cadence via dormantThreshold (spec 2026-07-25-rebooking-status).
const segmentOf = (last, cadence, visits) => {
  if (last == null) return "never";
  if (last < 90) return "active";
  if (last <= dormantThreshold({ cadence_days: cadence, visit_count: visits })) return "at_risk";
  return "dormant";
};

const cadenceStatusOf = (cadence, last) => {
  if (!cadence) return "none";
  if (last <= cadence) return "green";
  if (last <= cadence * 1.5) return "yellow";
  return "red";
};

export const mockCustomerRows = seeds.map(([user_id, name, visits, cadence, last, batch]) => ({
  user_id,
  customer_batch: batch,
  name,
  phone: `+1 416 555 0${String(user_id).slice(-3)}`,
  email: `${name.split(" ")[0].toLowerCase()}@example.com`,
  avatar: null,
  segment: segmentOf(last, cadence, visits),
  visit_count: visits,
  first_visit_date: last != null && visits > 0 ? d(last + (cadence || 0) * Math.max(visits - 1, 0)) : null,
  last_visit_date: last != null && visits > 0 ? d(last) : null,
  cadence_days: cadence,
  expected_date: cadence && last != null ? (last > cadence ? d(last - cadence) : inDays(cadence - last)) : null,
  cadence_status: visits > 0 ? cadenceStatusOf(cadence, last) : "none",
  overdue_days: cadence && last != null ? Math.max(0, last - cadence) : null,
  treatment_windows: [],
  last_contact:
    user_id % 3 === 0
      ? { type: "sms", date: d(10), actor_name: "Sam Reyes" }
      : null,
}));

export const mockConfig = { save_quiet_fallback_days: 60, save_followup_window_days: 30 };

// Same bucket rules as CRMPage's CustomerBookingFilters.
const matchesType = (row, type) => {
  if (!type || type === "AllCustomer") return true;
  const batch = row.customer_batch || "New User";
  if (type === "CancelMembers") return batch.includes("Expire/Cancel");
  if (type === "DeclineMembers") return batch.includes("Declined");
  if (type === "ActiveMembers")
    return !batch.includes("Expire/Cancel") && !batch.includes("Declined") && batch !== "New User";
  return true;
};

const matchesSearch = (row, search) =>
  !search ||
  row.name.toLowerCase().includes(search.toLowerCase()) ||
  (row.email || "").toLowerCase().includes(search.toLowerCase()) ||
  row.phone.replace(/\s/g, "").includes(search.replace(/\s/g, ""));

export const mockCustomers = (params = {}) => {
  const { segment, customer_type, search, page = 1, limit = 10 } = params;
  let rows = mockCustomerRows.filter(
    (r) =>
      (!segment || r.segment === segment) &&
      matchesType(r, customer_type) &&
      matchesSearch(r, search)
  );
  rows = [...rows].sort((a, b) => (b.overdue_days ?? -1) - (a.overdue_days ?? -1));
  const start = (page - 1) * limit;
  return {
    current_page: page,
    per_page: limit,
    total: rows.length,
    last_page: Math.max(1, Math.ceil(rows.length / limit)),
    data: rows.slice(start, start + limit),
  };
};

export const mockOverview = (params = {}) => {
  const counts = { active: 0, at_risk: 0, dormant: 0, never: 0 };
  mockCustomerRows
    .filter((r) => matchesType(r, params.customer_type) && matchesSearch(r, params.search))
    .forEach((r) => (counts[r.segment] += 1));
  return {
    active_count: counts.active,
    at_risk_count: counts.at_risk,
    dormant_count: counts.dormant,
    never_count: counts.never,
    saves_count: mockSaveRows.length,
    config: mockConfig,
  };
};

export const mockSaveRows = [
  {
    user_id: 9005,
    customer_name: "Ava Thompson",
    contact_date: moment().subtract(18, "days").toISOString(),
    contact_type: "call",
    actor_staff_id: 45,
    actor_name: "Sam Reyes",
    quiet_days_at_contact: 71,
    booking_date: d(6),
    booking_service: "Hydrafacial",
  },
  {
    user_id: 9007,
    customer_name: "Mason Lee",
    contact_date: moment().subtract(25, "days").toISOString(),
    contact_type: "sms",
    actor_staff_id: 12,
    actor_name: "Priya Shah",
    quiet_days_at_contact: 160,
    booking_date: d(9),
    booking_service: "Botox",
  },
];

export const mockSaves = (params = {}) => {
  const { page = 1, limit = 10 } = params;
  const start = (page - 1) * limit;
  return {
    current_page: page,
    per_page: limit,
    total: mockSaveRows.length,
    last_page: Math.max(1, Math.ceil(mockSaveRows.length / limit)),
    data: mockSaveRows.slice(start, start + limit),
  };
};

export const mockInsights = (userId) => {
  const row = mockCustomerRows.find((r) => String(r.user_id) === String(userId));
  return {
    spend_trend: { last_90d: 1240, prior_90d: 2030, pct_change: -38.9 },
    frequency_trend: { recent_per_90d: 1.0, alltime_per_90d: 2.2 },
    top_services: [
      { service_id: 9, name: "Hydrafacial", count: 6 },
      { service_id: 14, name: "Chemical Peel", count: 3 },
      { service_id: 2, name: "Botox", count: 2 },
    ],
    maturity: {
      first_visit_date: row?.first_visit_date || d(400),
      visit_count: row?.visit_count ?? 7,
    },
    cadence: row
      ? {
          cadence_days: row.cadence_days,
          last_visit_date: row.last_visit_date,
          expected_date: row.expected_date,
          cadence_status: row.cadence_status,
        }
      : null,
    upsell: [
      {
        have_service: "Hydrafacial",
        suggest_service: "Chemical Peel",
        suggest_service_id: 14,
        percentage: 68,
        sample_size: 143,
      },
    ],
    wallet_balance: 72.45,
    last_contact: row?.last_contact
      ? { ...row.last_contact, summary: "Left voicemail about rebooking" }
      : null,
    last_booking: row?.last_visit_date
      ? {
          date: row.last_visit_date,
          time: "02:30 PM",
          service: "Hydrafacial",
          staff_name: "Jess Nolan",
        }
      : null,
    membership:
      row?.customer_batch === "Member"
        ? {
            status: "active",
            plan_name: "Active $199 plan",
            price: 199,
            start_date: d(90),
            declined_date: null,
            renewal_date: inDays(10),
            visits_this_cycle: 0,
          }
        : { status: "none" },
    // Rule-based category windows render first on Insights; behavioral
    // service_windows only carry categories the rules DON'T match (backend
    // excludes matched ones so nothing shows twice — Botox now lives under
    // the injectables treatment window).
    treatment_windows: row?.treatment_windows || [],
    service_windows: [
      {
        service: "Hydrafacial",
        last_date: d(40),
        typical_interval_days: 30,
        due_date: d(10),
        status: "red",
      },
    ],
  };
};

let mockLogSeq = 1;
export const mockContactRows = [
  {
    id: mockLogSeq++,
    customer_id: 9005,
    actor_staff_id: 45,
    actor_name: "Sam Reyes",
    type: "call",
    summary: "Left voicemail about rebooking",
    created_at: moment().subtract(18, "days").toISOString(),
  },
];

export const mockContactLog = (params = {}) => {
  const rows = mockContactRows.filter(
    (r) => String(r.customer_id) === String(params.customer_id)
  );
  return { current_page: 1, per_page: 20, total: rows.length, last_page: 1, data: rows };
};

export const mockStoreContact = (payload) => {
  const row = {
    id: mockLogSeq++,
    actor_staff_id: 0,
    actor_name: "You",
    created_at: moment().toISOString(),
    summary: "",
    ...payload,
  };
  mockContactRows.unshift(row);
  return row;
};
